import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { HighlightOffOutlined, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import axios, { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';

import { mobileUsersApi, taskFormsApi } from '../../axios';
import { groupsApi } from '../../axios/groups';
import { useFeatureFlags, useLanguage } from '../../hooks';
import { ActivatedMobileUserMinimumData, UserGroup } from '../../types';
import {
  DynamicSignatureFormQuestion,
  RejectReason,
  SignatureOrderType,
  SignatureOrderUser,
  SignersStateGroups as SignersState,
  TaskFormSignatureDataGroups,
} from '../../types/taskForm';
import { AddIcon, EditIcon, TrashIcon, UserGroupIcon } from '../icons';
import { ConfirmActionDialog } from '../shadcn-ui/confirm-action-dialog';
import { InformationBanner } from '../uiComponents/CommonComponents/InformationBanner';
import { Loader } from '../uiComponents/CommonComponents/Loader';
import { Checkbox } from '../uiComponents/ComponentLibrary/Checkbox';
import { Switch } from '../uiComponents/ComponentLibrary/Switch';
import { SelectorChip } from '../uiComponents/selectorChip';
import { FirmaPricingEmptyState } from './FirmaPricingEmptyState';
import FormSignatureBuilderBar from './FormSignatureBuilderBar';
import { OwnerDrawerSelector } from './OwnerDrawerSelector';
import { SignatureConfigurationDrawerSelector } from './SignatureConfigurationDrawerSelector';
import { SignatureOrder } from './SignatureOrder';
import { QuorumValidationContext } from './SignatureOrderConfig';

const customStyles: Record<string, SxProps<Theme>> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '80px',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
  },
  paper: {
    margin: '20px',
    padding: '26px',
    borderRadius: '8px',
  },
  sectionTitleIcon: {
    fontSize: '24px',
    marginRight: '14px',
    color: (theme) => theme.palette.primary.main,
  },
  subsectionTitle: {
    display: 'flex',
    alignItems: 'center',
    margin: '30px 0 10px 0',
    fontSize: '20px',
    fontWeight: '500',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '16px',
    margin: '0',
    fontSize: '24px',
    fontWeight: '500',
    lineHeight: '1.1674',
  },
  addUsersLink: {
    display: 'flex',
    alignItems: 'center',
    color: 'primary.main',
    cursor: 'pointer',
    marginTop: '8px',
    marginBottom: '16px',
    userSelect: 'none',
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  },
  addIcon: {
    fontSize: '12px',
    marginRight: '6px',
  },
  usersSelectorContainer: {
    marginBottom: '14px',
  },
  ownerSelectorTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  infoIcon: {
    marginLeft: '4px',
    fontSize: '16px',
    color: '#888888',
  },
  requiredLabel: {
    marginBottom: '8px',
    '&::before': {
      content: '"*"',
      color: (theme) => theme.palette.error.main,
    },
  },
  switchFormControl: {
    marginLeft: '0px',
    display: 'flex',
    gap: '8px',
  },
  activateAutomatedSignatureContainer: {
    marginBottom: '16px',
  },
  authorMustSignCheckbox: {
    marginBottom: '16px',
  },
  rejectReasonsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    margin: '12px 0',
  },
  // Figma (Frame 2947): 32px tall, 8px radius, #D1D4D9 border, 8px h-padding.
  rejectReasonInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      height: '32px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#D1D4D9',
    },
    '& .MuiOutlinedInput-input': {
      padding: '0 8px',
      height: '32px',
      boxSizing: 'border-box',
    },
  },
  rejectReasonsBannerContainer: {
    margin: '12px 0',
  },
  rejectReasonsAddButton: {
    borderRadius: '24px',
    textTransform: 'none',
    marginTop: '4px',
  },
};

const groupKey = (group: SignatureOrderUser[]) =>
  group.map((u) => String(u.id)).sort().join('|');

// Realign the positional per-group quorum array to a new sequential order. Quorum values are
// keyed by the group's set of signers (not by position), so reordering/deleting/ungrouping
// carries each value to the group it belongs to and drops values for groups that no longer
// exist. A value >= the new group size becomes null ("all"). For the simultaneous case (no
// sequential groups) the array is left untouched — there the QuorumStepper reclamps on resize.
const realignMinimumSignatures = (
  oldOrder: SignatureOrderUser[][],
  oldMinSig: (number | null)[],
  newOrder: SignatureOrderUser[][],
): (number | null)[] => {
  if (newOrder.length === 0) return oldMinSig;
  const oldByKey = new Map<string, number | null>();
  oldOrder.forEach((group, i) => oldByKey.set(groupKey(group), oldMinSig[i] ?? null));
  return newOrder.map((group) => {
    const value = oldByKey.get(groupKey(group)) ?? null;
    if (value == null) return null;
    return value < group.length ? value : null;
  });
};

export function FormSignatureBuilder() {
  const lang = useLanguage();
  const { checkFlag } = useFeatureFlags();

  const { formId: formIdParam } = useParams();
  const formId = parseInt(formIdParam);
  const { enqueueSnackbar } = useSnackbar();
  const isDynamicSignatureEnabled = checkFlag('DynamicSignature');
  const isQuorumEnabled = checkFlag('SignatureGroupQuorum');
  // AC-6: the whole standardized-reject-reasons feature sits behind this flag so
  // it acts as a rollout kill switch. Off => config section hidden and the
  // reject_reasons payload key omitted (same pattern as quorum).
  const isStandardizedRejectReasonsEnabled = checkFlag('StandardizedRejectReasons');
  // Professional (plan gate): quorum is shown but blocked with an upgrade CTA.
  // Plan sin firma avanzada: el quórum se muestra pero bloqueado (el control se
  // renderiza deshabilitado con tooltip de upgrade, no un banner aparte).
  const isQuorumBlocked = checkFlag('SignatureGroupQuorumBlocked') && !isQuorumEnabled;

  const [isLoading, setLoading] = useState(true);
  const [signatureActive, setSignatureActive] = useState(false);
  const [signatureConfigurationDrawerOpen, setSignatureConfigurationDrawerOpen] = useState(false);
  const [required, setRequired] = useState(true);
  const [usersList, setUsersList] = useState<ActivatedMobileUserMinimumData[]>([]);
  const [groupsList, setGroupsList] = useState<UserGroup[]>([]);
  const [authorMustSign, setAuthorMustSign] = useState(false);
  const [locationActive, setLocationActive] = useState(false);
  const [signersState, setSignersState] = useState<SignersState>({
    selectedUsers: [],
    externalUsersEmails: [],
    sequentialOrder: [],
    simultaneousOrder: [],
    selectedQuestions: [],
  });
  const [questions, setQuestions] = useState<DynamicSignatureFormQuestion[]>([]);
  const [ownerDrawerOpen, setOwnerDrawerOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<ActivatedMobileUserMinimumData>(undefined);
  const [automatedSignature, setAutomatedSignature] = useState(false);
  const [minimumSignatures, setMinimumSignatures] = useState<(number | null)[]>([]);
  const [quorumErrorIds, setQuorumErrorIds] = useState<string[]>([]);

  // Reject reasons: local rows carry a negative temp id (isNew) until saved;
  // deletedReasonIds accumulates ids removed from an already-persisted row.
  // The whole list commits together with the rest of the tab on "Guardar".
  const [rejectReasons, setRejectReasons] = useState<(RejectReason & { isNew?: boolean })[]>([]);
  const [deletedReasonIds, setDeletedReasonIds] = useState<number[]>([]);
  const [reasonToDelete, setReasonToDelete] = useState<(RejectReason & { isNew?: boolean }) | null>(
    null,
  );
  // Monotonic negative ids for unsaved rows: Date.now() collides on fast double
  // clicks (two empty rows added in the same ms) producing duplicate React keys.
  const nextTempId = useRef(-1);

  const reportQuorumError = useCallback((id: string, invalid: boolean) => {
    setQuorumErrorIds((prev) => {
      const has = prev.includes(id);
      if (invalid && !has) return [...prev, id];
      if (!invalid && has) return prev.filter((errorId) => errorId !== id);
      return prev;
    });
  }, []);

  const hasQuorumError = quorumErrorIds.length > 0;
  const quorumValidationValue = useMemo(
    () => ({ reportError: reportQuorumError, errorIds: quorumErrorIds }),
    [reportQuorumError, quorumErrorIds],
  );

  const signatureOrderIsIncomplete =
    signersState.sequentialOrder.length > 0 && signersState.simultaneousOrder.length > 0;

  const signatureConfigHasSigners =
    authorMustSign ||
    signersState.simultaneousOrder.length > 0 ||
    signersState.sequentialOrder.length > 0;

  const getSelectedQuestionsForDrawer = (): DynamicSignatureFormQuestion[] => {
    return signersState.selectedQuestions;
  };

  const fixedSignerCount =
    signersState.selectedUsers.length +
    signersState.externalUsersEmails.length +
    (locationActive ? 1 : 0);

  const dynamicSignerCount = signersState.selectedQuestions.length;

  // Reactive per-row reject-reason validation (duplicate + empty persisted row).
  // Gated on the section being visible so a hidden config never blocks the tab.
  const reasonErrors = useMemo<Record<number, string>>(() => {
    if (!(signatureActive && isStandardizedRejectReasonsEnabled)) return {};
    const errors: Record<number, string> = {};
    const seen = new Map<string, number>();
    // Match MySQL utf8mb4_unicode_ci: case- and accent-insensitive. NFD splits
    // accents into combining marks (U+0300-U+036F), which we then strip.
    const combiningMarks = /[̀-ͯ]/g;
    const normalize = (text: string) =>
      text.trim().toLowerCase().normalize('NFD').replace(combiningMarks, '');
    rejectReasons.forEach((reason) => {
      const text = reason.reason.trim();
      if (!text) {
        // A blank NEW row is dropped on save; blanking a persisted row is an error.
        if (!reason.isNew) errors[reason.id] = lang.formSignatureBuilder.rejectReasonsEmptyError;
        return;
      }
      const key = normalize(reason.reason);
      if (seen.has(key)) {
        errors[reason.id] = lang.formSignatureBuilder.rejectReasonsDuplicateError;
      } else {
        seen.set(key, reason.id);
      }
    });
    return errors;
  }, [rejectReasons, signatureActive, isStandardizedRejectReasonsEnabled, lang]);

  const hasReasonErrors = Object.keys(reasonErrors).length > 0;

  const disableConfirm =
    (automatedSignature &&
      (!signatureConfigHasSigners || !selectedOwner || signatureOrderIsIncomplete)) ||
    hasQuorumError ||
    hasReasonErrors;

  const handleConfirm = async function () {
    try {
      if (signatureOrderIsIncomplete) {
        return;
      }

      // Reject-reason errors are surfaced per row (helperText) and gate the save
      // through disableConfirm -> the Guardar button, so there is no separate
      // guard here: it would be unreachable while the button is disabled.

      const isSequential =
        signersState.sequentialOrder.length > signersState.simultaneousOrder.length;
      let usersOrder: SignatureOrderUser[][];

      if (isSequential) {
        usersOrder = [...signersState.sequentialOrder];
      } else {
        usersOrder = [...signersState.simultaneousOrder];
      }

      const data = await taskFormsApi.updateTaskFormSignatureDataGroups(formId, {
        signable: signatureActive,
        required_signature: isQuorumEnabled ? true : required,
        author_must_sign: authorMustSign,
        mobile_user_ids: signersState.selectedUsers.map((user) => user.id),
        external_user_emails: signersState.externalUsersEmails,
        signature_requester_id: selectedOwner?.id,
        enabled_automated_signature: automatedSignature,
        question_ids: signersState.selectedQuestions.map((q) => q.id),
        ...(!signatureOrderIsIncomplete && {
          order: {
            type: isSequential ? SignatureOrderType.SEQUENTIAL : SignatureOrderType.SIMULTANEOUS,
            users: usersOrder,
          },
        }),
        ...(isQuorumEnabled && { minimum_signatures: minimumSignatures }),
        ...(isStandardizedRejectReasonsEnabled && {
          reject_reasons: {
            new: rejectReasons
              .filter((r) => r.isNew && r.reason.trim())
              .map((r) => r.reason.trim()),
            update: rejectReasons
              .filter((r) => !r.isNew && r.reason.trim())
              .map((r) => ({ id: r.id, reason: r.reason.trim() })),
            delete: deletedReasonIds,
          },
        }),
      });
      setSignatureActive(data.signable);
      // Fall back to the local list if the response omits reject_reasons (e.g.
      // flag off) so the kill switch never wipes the local view to "empty".
      setRejectReasons(data.reject_reasons ?? rejectReasons);
      setDeletedReasonIds([]);
      if (data.reject_reasons_errors?.length) {
        // Surface WHICH reasons failed (the backend returns the raw texts, not a
        // code). Function replacer so a reason containing $&, $` etc. is inserted
        // verbatim, not as a String.replace special pattern.
        const failed = data.reject_reasons_errors.join(', ');
        enqueueSnackbar(
          lang.formSignatureBuilder.rejectReasonsSaveError.replace('{reasons}', () => failed),
          { variant: 'warning' },
        );
      } else {
        enqueueSnackbar(lang.formSignatureBuilder.saveSuccess, { variant: 'success' });
      }
    } catch (error) {
      let errorMessage = lang.formSignatureBuilder.saveErrorReload;
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response && axiosError.response.status === 403) {
          const data = axiosError.response.data as TaskFormSignatureDataGroups;
          setSignatureActive(data?.signable);
          errorMessage = !data?.account_config_present
            ? lang.formSignatureBuilder.accountSignatureConfigError
            : lang.formSignatureBuilder.saveError;
        }
        console.error('Error:', axiosError.message);
      } else {
        console.error('Error:', error);
      }
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const addLocationIdToUser = (users: SignatureOrderUser[]): SignatureOrderUser[] => {
    return users.map((user) => {
      if (user.is_location) {
        return { id: 'location', is_external: false, is_location: true, question_id: null };
      }
      return user;
    });
  };

  const createDynamicSignerUser = (
    questionId: number,
    sequenceOrder: number | null = null,
  ): SignatureOrderUser => {
    return {
      id: `q_${questionId}`,
      is_external: false,
      is_location: false,
      question_id: questionId,
      sequence_order: sequenceOrder,
    };
  };

  const removeUserFromOrder = (order: SignatureOrderUser[][], id: number | string) => {
    return order.reduce((acc, users) => {
      const newUsers = users.filter((user) => {
        if (typeof id === 'string' && id.startsWith('q_')) {
          const questionId = parseInt(id.replace('q_', ''));
          return String(user.question_id) !== String(questionId);
        }
        return String(user.id) !== String(id);
      });
      if (newUsers.length > 0) {
        acc.push(newUsers);
      }
      return acc;
    }, [] as SignatureOrderUser[][]);
  };

  const addMissingDynamicSignersToOrder = (
    usersOrder: SignatureOrderUser[][],
    missingDynamicSigners: DynamicSignatureFormQuestion[],
    orderType: SignatureOrderType,
  ): SignatureOrderUser[][] => {
    if (missingDynamicSigners.length === 0) {
      return usersOrder;
    }

    const isSequential = orderType === SignatureOrderType.SEQUENTIAL;
    const dynamicSignersToAdd: SignatureOrderUser[][] = missingDynamicSigners.map(
      (question, index) => [
        createDynamicSignerUser(question.id, isSequential ? usersOrder.length + index : null),
      ],
    );

    return [...usersOrder, ...dynamicSignersToAdd];
  };

  const handleAuthorMustSignChange = (value: boolean) => {
    setAuthorMustSign(value);

    if (value) {
      const targetOrder =
        signersState.simultaneousOrder.length >= signersState.sequentialOrder.length
          ? 'simultaneousOrder'
          : 'sequentialOrder';
      const authorAlreadyInOrder = signersState[targetOrder].some((group) =>
        group.some((user) => user.id === 'author'),
      );

      if (authorAlreadyInOrder) return;
      setSignersState((prev) => ({
        ...prev,
        [targetOrder]: [
          ...prev[targetOrder],
          [{ id: 'author', is_external: false, is_location: false, question_id: null }],
        ],
      }));
    } else {
      const newSequentialOrder = removeUserFromOrder(signersState.sequentialOrder, 'author');
      const newSimultaneousOrder = removeUserFromOrder(signersState.simultaneousOrder, 'author');
      setSignersState((prev) => ({
        ...prev,
        sequentialOrder: newSequentialOrder,
        simultaneousOrder: newSimultaneousOrder,
      }));
    }
  };

  // Reasons are edited inline (each row is an input, per Figma). A blank new row
  // is appended on "Agregar"; empty NEW rows are dropped on save, empty persisted
  // rows and case-insensitive duplicates are blocked with a per-row message.
  const handleAddReason = () => {
    setRejectReasons((prev) => [...prev, { id: nextTempId.current--, reason: '', isNew: true }]);
  };

  const handleReasonTextChange = (id: number, value: string) => {
    setRejectReasons((prev) => prev.map((r) => (r.id === id ? { ...r, reason: value } : r)));
    // Per-row errors clear reactively via the reasonErrors useMemo as text changes.
  };

  const handleConfirmDeleteReason = () => {
    if (!reasonToDelete) return;
    setRejectReasons((prev) => prev.filter((r) => r.id !== reasonToDelete.id));
    if (!reasonToDelete.isNew) {
      setDeletedReasonIds((prev) => [...prev, reasonToDelete.id]);
    }
    setReasonToDelete(null);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const [signatureData, mobileUsers, groupData] = await Promise.all([
          taskFormsApi.getTaskFormSignatureDataGroups(formId),
          mobileUsersApi.getAllActivatedMobileUsers(),
          groupsApi.getGroups(),
        ]);

        // The signer selectors must not depend on the order processing below: if
        // that payload comes in incomplete, the lists stay available anyway.
        setUsersList(mobileUsers);
        setGroupsList(groupData?.groups ?? []);

        const dynamicSignatureFormQuestions = isDynamicSignatureEnabled
          ? await taskFormsApi.getDynamicSignatureTaskFormQuestions(formId)
          : [];

        const locationUser = signatureData.order?.users?.some((group) =>
          group.some((user) => user.is_location),
        );

        const authorIsNotInOrder = !signatureData.order?.users?.some((group) =>
          group.some((user) => user.id === 'author'),
        );
        let usersOrder: SignatureOrderUser[][] = signatureData.order?.users
          ? signatureData.order.users.map((group) => addLocationIdToUser(group))
          : [];

        if (authorIsNotInOrder && signatureData.author_must_sign) {
          usersOrder.unshift([
            { id: 'author', is_external: false, is_location: false, question_id: null },
          ]);
        }

        const selectedQuestions: DynamicSignatureFormQuestion[] = signatureData.questions ?? [];
        const currentDynamicSignatureQuestionIds = new Set(
          usersOrder.flatMap((group) =>
            group.filter((user) => user.question_id != null).map((user) => user.question_id),
          ),
        );
        const missingDynamicSigners = selectedQuestions.filter(
          (question) => !currentDynamicSignatureQuestionIds.has(question.id),
        );
        usersOrder = addMissingDynamicSignersToOrder(
          usersOrder,
          missingDynamicSigners,
          signatureData.order?.type,
        );

        setSignatureActive(signatureData.signable);
        setRequired(isQuorumEnabled ? true : signatureData.required_signature);
        setAuthorMustSign(signatureData.author_must_sign);
        setAutomatedSignature(signatureData.enabled_automated_signature ?? false);
        setLocationActive(locationUser);
        setSignersState({
          selectedUsers: mobileUsers.filter((user) =>
            (signatureData.mobile_users ?? []).some((mobileUser) => mobileUser.id === user.id),
          ),
          externalUsersEmails: signatureData.external_users ?? [],
          sequentialOrder:
            signatureData.order?.type === SignatureOrderType.SEQUENTIAL ? usersOrder : [],
          simultaneousOrder:
            signatureData.order?.type === SignatureOrderType.SIMULTANEOUS ||
            !signatureData.order?.type
              ? usersOrder
              : [],
          selectedQuestions: selectedQuestions,
        });
        setSelectedOwner(
          mobileUsers.find((user) => signatureData.signature_requester?.id === user.id),
        );
        setMinimumSignatures(signatureData.order?.minimum_signatures ?? []);
        setQuestions(dynamicSignatureFormQuestions);
        setRejectReasons(signatureData.reject_reasons ?? []);
      } catch (error) {
        const errorMessage = lang.tasksForms.error_form;
        enqueueSnackbar(errorMessage, { variant: 'error' });
        console.error('Error fetching form signature data:', error);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  const handleFixedSignersSelected = (
    users: ActivatedMobileUserMinimumData[],
    externalUsers: string[],
    isLocationActive: boolean,
  ) => {
    const currentSigners = new Set([
      ...signersState.sequentialOrder.flatMap((users) => users.map((user) => user.id)),
      ...signersState.simultaneousOrder.flatMap((users) => users.map((user) => user.id)),
    ]);

    const updatedSigners: SignatureOrderUser[][] = [
      ...users.map((user) => [
        {
          id: user.id,
          is_external: false,
          is_location: false,
          question_id: null,
        } as SignatureOrderUser,
      ]),
      ...externalUsers.map((email) => [
        {
          id: email,
          is_external: true,
          is_location: false,
          question_id: null,
        } as SignatureOrderUser,
      ]),
    ];

    setLocationActive(isLocationActive);
    if (isLocationActive) {
      updatedSigners.push([
        { id: 'location', is_external: false, is_location: true, question_id: null },
      ]);
    }

    if (authorMustSign) {
      updatedSigners.push([
        { id: 'author', is_external: false, is_location: false, question_id: null },
      ]);
    }

    const validSignerIds = new Set<string | number>([
      ...users.map((user) => user.id),
      ...externalUsers,
      ...(isLocationActive ? ['location'] : []),
      ...(authorMustSign ? ['author'] : []),
      ...signersState.selectedQuestions.map((question) => `q_${question.id}`),
    ]);

    const newSigners = updatedSigners.filter((users) =>
      users.some((user) => !currentSigners.has(user.id)),
    );

    const targetOrder =
      signersState.simultaneousOrder.length >= signersState.sequentialOrder.length
        ? 'simultaneousOrder'
        : 'sequentialOrder';

    const newSequentialOrder = signersState.sequentialOrder
      .map((oldUsers) => {
        return oldUsers.filter((oldUser) => {
          if (oldUser.question_id !== null) {
            const dynamicSignerId = `q_${oldUser.question_id}`;
            return validSignerIds.has(dynamicSignerId);
          }
          return validSignerIds.has(oldUser.id);
        });
      })
      .filter((group) => group.length > 0);

    const newSimultaneousOrder = signersState.simultaneousOrder
      .map((oldUsers) => {
        return oldUsers.filter((oldUser) => {
          if (oldUser.question_id !== null) {
            const dynamicSignerId = `q_${oldUser.question_id}`;
            return validSignerIds.has(dynamicSignerId);
          }
          return validSignerIds.has(oldUser.id);
        });
      })
      .filter((group) => group.length > 0);

    if (targetOrder === 'simultaneousOrder') {
      newSimultaneousOrder.push(...newSigners);
    } else {
      newSequentialOrder.push(...newSigners);
    }

    setSignersState({
      ...signersState,
      selectedUsers: users,
      externalUsersEmails: externalUsers,
      selectedQuestions: signersState.selectedQuestions,
      sequentialOrder: newSequentialOrder,
      simultaneousOrder: newSimultaneousOrder,
    });
  };

  const updateSignersState = (
    signerIdToRemove: number | string,
    overrides: Partial<SignersState> = {},
  ) => {
    const newSequentialOrder = removeUserFromOrder(signersState.sequentialOrder, signerIdToRemove);
    const newSimultaneousOrder = removeUserFromOrder(
      signersState.simultaneousOrder,
      signerIdToRemove,
    );

    setMinimumSignatures(
      realignMinimumSignatures(signersState.sequentialOrder, minimumSignatures, newSequentialOrder),
    );
    setSignersState({
      ...signersState,
      sequentialOrder: newSequentialOrder,
      simultaneousOrder: newSimultaneousOrder,
      ...overrides,
    });
  };

  const handleLocationUserDelete = () => {
    setLocationActive(false);
    updateSignersState('location');
  };

  const handleAuthorUserDelete = () => {
    setAuthorMustSign(false);
    updateSignersState('author');
  };

  const handleInternalAndExternalUsersDelete = (signerId: number | string) => {
    updateSignersState(signerId, {
      selectedUsers: signersState.selectedUsers.filter(
        (user) => String(user.id) !== String(signerId),
      ),
      externalUsersEmails: signersState.externalUsersEmails.filter(
        (email) => email !== String(signerId),
      ),
    });
  };

  const handleSignerDelete = (signerId: number | string) => {
    if (typeof signerId === 'string' && signerId.startsWith('q_')) {
      const questionId = parseInt(signerId.replace('q_', ''));
      handleQuestionUserDelete(questionId);
      return;
    }

    if (signerId === 'location') {
      handleLocationUserDelete();
      return;
    }

    if (signerId === 'author') {
      handleAuthorUserDelete();
      return;
    }

    handleInternalAndExternalUsersDelete(signerId);
  };

  const handleQuestionUserDelete = (questionId: number) => {
    const questionUserId = `q_${questionId}`;
    const newSelectedQuestions = signersState.selectedQuestions.filter(
      (q) => String(q.id) !== String(questionId),
    );

    updateSignersState(questionUserId, {
      selectedQuestions: newSelectedQuestions,
    });
  };

  const handleSignersOrderChange = (
    sequentialOrder: SignatureOrderUser[][],
    simultaneousOrder: SignatureOrderUser[][],
  ) => {
    setMinimumSignatures(
      realignMinimumSignatures(signersState.sequentialOrder, minimumSignatures, sequentialOrder),
    );
    setSignersState({
      ...signersState,
      sequentialOrder,
      simultaneousOrder,
      selectedQuestions: signersState.selectedQuestions,
    });
  };

  const handleRequiredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isRequired = event.target.value === 'required';
    setRequired(isRequired);

    if (!isRequired) {
      setSignersState({
        ...signersState,
        simultaneousOrder: [
          ...signersState.simultaneousOrder.flat().map((user) => [user]),
          ...signersState.sequentialOrder.flat().map((user) => [user]),
        ],
        sequentialOrder: [],
        selectedQuestions: signersState.selectedQuestions,
      });
    }
  };

  const handleDynamicSignersSelected = (selectedQuestions: DynamicSignatureFormQuestion[]) => {
    const questionIds = selectedQuestions.map((question) => question.id);

    setSignersState((prev) => {
      const deselectedQuestionIds = prev.selectedQuestions
        .filter((prevQ) => !questionIds.includes(prevQ.id))
        .map((q) => q.id);

      const existingQuestionIds = prev.selectedQuestions.map((q) => q.id);
      const newQuestions = selectedQuestions.filter((q) => !existingQuestionIds.includes(q.id));

      let newSequentialOrder = prev.sequentialOrder;
      let newSimultaneousOrder = prev.simultaneousOrder;

      deselectedQuestionIds.forEach((questionId) => {
        newSequentialOrder = removeUserFromOrder(newSequentialOrder, `q_${questionId}`);
        newSimultaneousOrder = removeUserFromOrder(newSimultaneousOrder, `q_${questionId}`);
      });

      const isSequential = prev.sequentialOrder.length > prev.simultaneousOrder.length;

      const newQuestionUsers: SignatureOrderUser[][] = newQuestions.map((q, index) => {
        const currentOrderLength = isSequential
          ? newSequentialOrder.length
          : newSimultaneousOrder.length;

        return [
          {
            id: `q_${q.id}`,
            is_external: false,
            is_location: false,
            question_id: q.id,
            sequence_order: isSequential ? currentOrderLength + index : null,
          },
        ];
      });

      if (isSequential) {
        newSequentialOrder = [...newSequentialOrder, ...newQuestionUsers];
      } else {
        newSimultaneousOrder = [...newSimultaneousOrder, ...newQuestionUsers];
      }

      return {
        ...prev,
        selectedQuestions: selectedQuestions,
        sequentialOrder: newSequentialOrder,
        simultaneousOrder: newSimultaneousOrder,
      };
    });
  };

  if (isLoading) return <Loader />;

  // Plan gate (SignaturePricing): a :none account has no signature access, so
  // the config surface shows the pricing empty state instead of letting them
  // configure signers and fail on save.
  if (checkFlag('SignatureNotInPlan')) {
    return <FirmaPricingEmptyState />;
  }

  return (
    <QuorumValidationContext.Provider value={quorumValidationValue}>
    <Box sx={customStyles.container}>
      <Box sx={customStyles.content}>
        <Paper
          elevation={1}
          sx={customStyles.paper}
        >
          <Box>
            <Typography sx={customStyles.sectionTitle}>
              <EditIcon sx={customStyles.sectionTitleIcon} />
              {lang.formSignatureBuilder.signatureTitle}
            </Typography>
            <FormControlLabel
              sx={customStyles.switchFormControl}
              control={
                <Switch
                  checked={signatureActive}
                  disableRipple
                  inputProps={
                    {
                      'aria-label': 'controlled',
                      'data-testid': 'signature-activate-switch',
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              onChange={() => setSignatureActive(!signatureActive)}
              label={lang.formSignatureBuilder.checkboxSignatureActivate}
            />
          </Box>
          {signatureActive && !isQuorumEnabled && (
            <Box>
              <Typography sx={customStyles.subsectionTitle}>
                {lang.formSignatureBuilder.generalSettings}
              </Typography>
              <RadioGroup
                value={required ? 'required' : 'optional'}
                onChange={handleRequiredChange}
              >
                <FormControlLabel
                  value='required'
                  control={<Radio />}
                  label={lang.formSignatureBuilder.mandatorySignature}
                />
                <FormControlLabel
                  value='optional'
                  control={<Radio />}
                  label={lang.formSignatureBuilder.optionalSignature}
                />
              </RadioGroup>
            </Box>
          )}
        </Paper>
        {signatureActive && (
          <Paper
            elevation={1}
            sx={customStyles.paper}
          >
            <Typography sx={customStyles.sectionTitle}>
              <UserGroupIcon sx={customStyles.sectionTitleIcon} />
              {lang.formSignatureBuilder.signersSettings}
            </Typography>
            <Box sx={customStyles.activateAutomatedSignatureContainer}>
              <FormControlLabel
                sx={customStyles.switchFormControl}
                control={
                  <Switch
                    checked={automatedSignature}
                    disableRipple
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
                onChange={() => setAutomatedSignature(!automatedSignature)}
                label={lang.formSignatureBuilder.automatedSignature}
              />
            </Box>
            {automatedSignature && (
              <>
                <FormControlLabel
                  control={<Checkbox inputProps={{ 'aria-label': 'controlled' }} />}
                  checked={authorMustSign}
                  onChange={(_, value) => handleAuthorMustSignChange(value)}
                  label={lang.formSignatureBuilder.authorMustSign}
                  sx={customStyles.authorMustSignCheckbox}
                />
                <Box sx={customStyles.usersSelectorContainer}>
                  <Typography
                    sx={
                      {
                        ...customStyles.ownerSelectorTitle,
                        ...customStyles.requiredLabel,
                      } as SxProps<Theme>
                    }
                  >
                    {lang.formSignatureBuilder.selectResponsible}
                    <Tooltip title={lang.formSignatureBuilder.selectResponsibleTooltip}>
                      <InfoOutlined sx={customStyles.infoIcon} />
                    </Tooltip>
                  </Typography>
                  {selectedOwner ? (
                    <Box sx={customStyles.chipContainer}>
                      <SelectorChip
                        key={selectedOwner?.id}
                        name={selectedOwner.name?.trim() || selectedOwner.email}
                        onDelete={() => setSelectedOwner(undefined)}
                      />
                    </Box>
                  ) : (
                    <Box onClick={() => setOwnerDrawerOpen(true)}>
                      <Typography
                        variant='body2'
                        color='primary'
                        sx={customStyles.addUsersLink}
                      >
                        <AddIcon
                          sx={customStyles.addIcon}
                          fill='primary'
                          color='primary'
                        />
                        {lang.formSignatureBuilder.addResponsible}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={customStyles.usersSelectorContainer}>
                  <Typography sx={customStyles.requiredLabel}>
                    {lang.formSignatureBuilder.selectUsersToSign}
                  </Typography>
                  <Box sx={customStyles.chipContainer}>
                    {signersState.selectedUsers.map((user) => (
                      <SelectorChip
                        key={user.id}
                        name={user.name?.trim() || user.email}
                        onDelete={() => handleSignerDelete(user.id)}
                      />
                    ))}
                    {signersState.externalUsersEmails.map((email) => (
                      <SelectorChip
                        key={email}
                        name={email}
                        onDelete={() => handleSignerDelete(email)}
                      />
                    ))}
                    {signersState.selectedQuestions.map((question) => (
                      <SelectorChip
                        key={question.id}
                        name={question.title}
                        onDelete={() => handleQuestionUserDelete(question.id)}
                      />
                    ))}
                  </Box>
                  <Box onClick={() => setSignatureConfigurationDrawerOpen(true)}>
                    <Typography
                      variant='body2'
                      color='primary'
                      sx={customStyles.addUsersLink}
                    >
                      <AddIcon
                        sx={customStyles.addIcon}
                        fill='primary'
                        color='primary'
                      />
                      {lang.formSignatureBuilder.addSigners}
                    </Typography>
                  </Box>
                  <SignatureOrder
                    signersState={signersState}
                    onSignersOrderChange={handleSignersOrderChange}
                    onSignerDelete={handleSignerDelete}
                    disabled={!required && !isQuorumEnabled}
                    isLocationActive={locationActive}
                    authorMustSign={authorMustSign}
                    isQuorumEnabled={isQuorumEnabled}
                    quorumBlocked={isQuorumBlocked}
                    minimumSignatures={minimumSignatures}
                    onMinimumSignaturesChange={setMinimumSignatures}
                  />
                </Box>
              </>
            )}
          </Paper>
        )}
        {signatureActive && isStandardizedRejectReasonsEnabled && (
          <Paper
            elevation={1}
            sx={customStyles.paper}
            data-testid='reject-reasons-section'
          >
            <Typography sx={customStyles.sectionTitle}>
              <HighlightOffOutlined sx={customStyles.sectionTitleIcon} />
              {lang.formSignatureBuilder.rejectReasonsTitle}
            </Typography>
            <Box sx={customStyles.rejectReasonsBannerContainer}>
              <InformationBanner
                type='info'
                message={lang.formSignatureBuilder.rejectReasonsInfoBanner}
              />
            </Box>
            {rejectReasons.length === 0 && (
              <Typography variant='body2'>
                {lang.formSignatureBuilder.rejectReasonsEmptyText}
              </Typography>
            )}
            <Box sx={customStyles.rejectReasonsList}>
              {rejectReasons.map((reason) => (
                <TextField
                  key={reason.id}
                  fullWidth
                  size='small'
                  value={reason.reason}
                  placeholder={lang.formSignatureBuilder.rejectReasonsAddPlaceholder}
                  onChange={(e) => handleReasonTextChange(reason.id, e.target.value)}
                  error={!!reasonErrors[reason.id]}
                  helperText={reasonErrors[reason.id]}
                  sx={customStyles.rejectReasonInput}
                  data-testid={`reject-reason-input-${reason.id}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          size='small'
                          onClick={() => setReasonToDelete(reason)}
                          data-testid={`reject-reason-delete-${reason.id}`}
                        >
                          {/* TrashIcon defaults to a white fill (invisible on the
                              white input); use primary blue to match Figma. */}
                          <TrashIcon
                            fill='#3E8FFA'
                            sx={{ fontSize: '18px' }}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ))}
            </Box>
            <Button
              variant='outlined'
              color='primary'
              fullWidth
              sx={customStyles.rejectReasonsAddButton}
              onClick={handleAddReason}
              data-testid='reject-reason-add-button'
            >
              {lang.formSignatureBuilder.rejectReasonsAddButton}
            </Button>
          </Paper>
        )}
        <ConfirmActionDialog
          isOpen={!!reasonToDelete}
          onClose={() => setReasonToDelete(null)}
          onConfirmAction={handleConfirmDeleteReason}
          severity='warning'
          warningTitle={lang.formSignatureBuilder.rejectReasonsDeleteTitle}
          warningMessage={lang.formSignatureBuilder.rejectReasonsDeleteDescription}
          cancelActionButtonText={lang.formSignatureBuilder.rejectReasonsDeleteCancel}
          confirmActionButtonText={lang.formSignatureBuilder.rejectReasonsDeleteConfirm}
          dialogTestId='reject-reason-delete-confirm-dialog'
          cancelButtonTestId='reject-reason-delete-cancel'
          confirmButtonTestId='reject-reason-delete-confirm'
          dismissOnOverlayClick
          compactHeader
        />
        {signatureConfigurationDrawerOpen && (
          <SignatureConfigurationDrawerSelector
            open={signatureConfigurationDrawerOpen}
            onClose={() => setSignatureConfigurationDrawerOpen(false)}
            users={usersList}
            groups={groupsList}
            onFixedSignersSelected={handleFixedSignersSelected}
            initialSelectedUsers={signersState.selectedUsers}
            initialExternalUsers={signersState.externalUsersEmails}
            initialLocationChecked={locationActive}
            fixedSignerCount={fixedSignerCount}
            dynamicSignerCount={dynamicSignerCount}
            dynamicSignatureQuestionsAvailable={questions}
            dynamicSignatureQuestionsSelected={getSelectedQuestionsForDrawer()}
            onDynamicSignersSelected={handleDynamicSignersSelected}
          />
        )}

        {ownerDrawerOpen && (
          <OwnerDrawerSelector
            users={usersList}
            groups={groupsList}
            onSave={(users) => setSelectedOwner(users[0])}
            onClose={() => setOwnerDrawerOpen(false)}
            open={ownerDrawerOpen}
            initialSelectedUsers={selectedOwner ? [selectedOwner] : []}
          />
        )}
      </Box>
      <FormSignatureBuilderBar
        onConfirm={handleConfirm}
        disabled={disableConfirm}
      />
    </Box>
    </QuorumValidationContext.Provider>
  );
}
